import { inject, Injector, runInInjectionContext } from "@angular/core";
import { CanActivateFn, Routes } from "@angular/router";
import { TryPrependAFDRouteMatchGuard } from "src/app/guards/try-prepend-afd-route-match-guard";
import {
  StandardLoggedInGuard,
  StandardLogoutGuard,
} from "src/standard/auth/auth";
import { SecurityActivateGuard } from "src/standard/security/security-activate.guard";
import workspaceRoutes from "./workspace/routes";
import { firstValueFrom, isObservable } from "rxjs";
import { SecurityService } from "src/standard/security/security.service";
import { isLoggingOut } from "src/app/services/isLoggingOut";

const TypedStandardLoggedInGuard: CanActivateFn = StandardLoggedInGuard;

const TryLoggedInGuard: CanActivateFn = async (next, state) => {
  const injector = inject(Injector);

  const res = await TypedStandardLoggedInGuard(next, state);
  if (res === true) {
    // log in
    await runInInjectionContext(injector, async () => {
      await SecurityActivateGuard(next, state);
    });
  }

  if (isObservable(res)) {
    return await firstValueFrom(res);
  } else {
    return res;
  }
};

const routes: Routes = [
  {
    path: "",
    redirectTo: "/ws",
    pathMatch: "full",
  },
  {
    path: "oauth2-redirect",
    loadComponent: () =>
      import(
        "src/standard/pages/auth/oauth2-redirect/oauth2-redirect.component"
      ),
  },
  {
    path: "maintenance",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/maintenance/maintenance.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "login",
    loadComponent: () => import("src/standard/pages/login.component"),
    canActivate: [StandardLoggedInGuard],
  },
  {
    // We do this via a URL route
    // so that any CanDeactivate guards can run for pending changes first
    path: "logout",
    loadComponent: () =>
      import("src/standard/standard-empty/standard-empty.component"),
    canActivate: [
      () => {
        const security = inject(SecurityService);

        // This needs to be called before logout to allow other tabs to initiate their cleanup
        // before the auth token is destroyed.
        security.notifyLoggedOut();

        isLoggingOut.set(true);
      },
      StandardLogoutGuard,
    ],
  },
  {
    path: "change-locale",
    loadComponent: () => import("src/standard/pages/change-locale.component"),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "access-denied",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/access-denied/access-denied.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "firm-not-onboarded",
    loadComponent: () =>
      import(
        "src/standard/pages/error-pages/firm-not-onboarded/firm-not-onboarded.component"
      ),
    canActivate: [TryLoggedInGuard],
  },
  {
    path: "",
    canActivate: [StandardLoggedInGuard],
    children: [
      {
        path: "ws",
        children: workspaceRoutes,
      },
      {
        path: "downloads",
        loadChildren: () => import("./nav-content/download-list/routes"),
      },
      {
        path: "design-system",
        loadChildren: () => import("../standard/pages/design-system/routes"),
      },
      {
        path: "development",
        loadChildren: () => import("../standard/pages/development/routes"),
      },
      // in cases where we're missing the afdRoute,
      // we can redirect to the path with the afdRoute included
      {
        path: "**",
        loadComponent: () =>
          import("src/standard/standard-empty/standard-empty.component"),
        canMatch: [TryPrependAFDRouteMatchGuard],
      },
      {
        path: "**",
        loadComponent: () =>
          import(
            "src/standard/pages/error-pages/page-not-found/page-not-found.component"
          ),
        canActivate: [TryLoggedInGuard],
      },
    ],
  },
];

export default routes;
